import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { PiFloppyDiskBold, PiPencilSimpleBold, PiXBold, PiInfoBold, PiPlusBold, PiTrashBold } from 'react-icons/pi';
import { PageHeader, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';

type PaymentRule = {
    id?: number;
    payment_condition: string;
    payment_label: string;
    installments_max: number;
    seller_alcada: number;
    seller_base_rate: number;
    manager_alcada: number;
    manager_adjusted_rate: number;
    director_alcada: number;
    director_adjusted_rate: number;
    discount_floor: number;
    rate_per_pct_above_floor: number;
    active: boolean;
    sort_order: number;
};

type Step = { low: number; high: number; rate: number; isMin: boolean };

const NUM_STEPS = 5;
const TOTAL_COLS = 1 + 1 + 1 + 1 + NUM_STEPS * 2 + 1; // condição + teto_v + teto_g + base + steps + ações

const TH_NAVY = 'bg-[#1a2744] text-white text-center text-[11px] font-semibold px-2 py-2 border border-[#263359]';
const TD = 'text-center text-sm px-2 py-2 border border-gray-100';

function fmtN(v: number) {
    // Formata número sem zeros desnecessários, troca ponto por vírgula
    return v % 1 === 0 ? String(v) : v.toFixed(1).replace('.', ',');
}
function fmtPct(v: number) {
    return (v * 100).toFixed(1).replace('.', ',') + '%';
}

function computeSteps(rule: PaymentRule): Step[] {
    const floor = rule.discount_floor ?? rule.manager_alcada;
    const perPct = rule.rate_per_pct_above_floor ?? 0.001;
    const numSteps = Math.round(rule.director_alcada - floor);
    const steps: Step[] = [];
    for (let i = 1; i <= numSteps; i++) {
        const rate = Math.max(rule.director_adjusted_rate, rule.seller_base_rate - i * perPct);
        const isMin = Math.abs(rate - rule.director_adjusted_rate) < 0.00001;
        steps.push({
            low: floor + (i - 1) === 0 ? 0.1 : floor + (i - 1) + 0.1,
            high: floor + i,
            rate,
            isMin,
        });
    }
    return steps;
}

function FieldEdit({
    label,
    value,
    onChange,
    isRate = false,
    suffix = '',
}: {
    label: string;
    value: number;
    onChange: (v: number) => void;
    isRate?: boolean;
    suffix?: string;
}) {
    const display = isRate ? parseFloat((value * 100).toFixed(4)) : value;
    return (
        <label className="flex flex-col gap-0.5 min-w-[90px]">
            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
            <div className="flex items-center gap-0.5">
                <input
                    type="number"
                    step={isRate ? '0.1' : '1'}
                    min={0}
                    max={isRate ? 20 : 100}
                    value={display}
                    onChange={(e) =>
                        onChange(isRate ? parseFloat(e.target.value) / 100 : parseFloat(e.target.value))
                    }
                    className="w-20 rounded border border-orange-300 bg-white px-2 py-1 text-center text-sm font-semibold focus:border-orange-500 focus:outline-none"
                />
                {suffix && <span className="text-xs text-gray-500">{suffix}</span>}
            </div>
        </label>
    );
}

function RuleRow({
    rule,
    editingId,
    onEdit,
    onCancel,
    onChange,
    onSave,
    onDelete,
    isSaving,
    isDeleting,
}: {
    rule: PaymentRule;
    editingId: string | null;
    onEdit: (id: string) => void;
    onCancel: () => void;
    onChange: (field: keyof PaymentRule, value: unknown) => void;
    onSave: () => void;
    onDelete: (id: number) => void;
    isSaving: boolean;
    isDeleting: boolean;
}) {
    const isEditing = editingId === rule.payment_condition;
    const steps = computeSteps(rule);
    const padded: (Step | null)[] = [
        ...steps,
        ...Array(Math.max(0, NUM_STEPS - steps.length)).fill(null),
    ];

    return (
        <>
            <tr className={`transition-colors ${isEditing ? 'bg-orange-50 ring-1 ring-orange-300 ring-inset' : 'hover:bg-gray-50/60'}`}>
                {/* Condição */}
                <td className={`${TD} text-left font-semibold text-gray-900 whitespace-nowrap pl-4`}>
                    <div>{rule.payment_label}</div>
                    <div className="text-[10px] text-gray-400 font-normal">{rule.payment_condition}</div>
                </td>

                {/* Teto Desc. Vendedor */}
                <td className={TD}>
                    <span className="inline-flex items-center rounded-full bg-green-100 text-green-800 border border-green-200 px-2 py-0.5 text-xs font-semibold">
                        até {rule.seller_alcada}%
                    </span>
                </td>

                {/* Teto Desc. Gerência */}
                <td className={`${TD} bg-yellow-50`}>
                    <span className="inline-flex items-center rounded-full bg-yellow-200 text-yellow-900 border border-yellow-300 px-2 py-0.5 text-xs font-semibold">
                        até {rule.manager_alcada}%
                    </span>
                </td>

                {/* Comissão Base (desconto 0 a teto gerência) */}
                <td className={`${TD} bg-green-50`}>
                    <div className="font-bold text-green-800 text-sm">{fmtPct(rule.seller_base_rate)}</div>
                    <div className="text-[10px] text-green-600">
                        0% a {fmtN(rule.manager_alcada)}%
                    </div>
                </td>

                {/* Steps de diretoria */}
                {padded.map((step, i) =>
                    !step ? (
                        <React.Fragment key={i}>
                            <td className={`${TD} bg-red-50 text-gray-300 text-xs`} colSpan={2}>—</td>
                        </React.Fragment>
                    ) : (
                        <React.Fragment key={i}>
                            {/* Faixa */}
                            <td className={`${TD} bg-red-50 text-[11px] text-red-700 font-medium whitespace-nowrap`}>
                                {fmtN(step.low)}% a {fmtN(step.high)}%
                            </td>
                            {/* Taxa */}
                            <td
                                className={`${TD} text-xs font-bold ${
                                    step.isMin
                                        ? 'bg-red-800 text-white'
                                        : 'bg-red-600 text-white'
                                }`}
                            >
                                {fmtPct(step.rate)}
                                {step.isMin && (
                                    <div className="text-[9px] opacity-70 font-normal">mín</div>
                                )}
                            </td>
                        </React.Fragment>
                    ),
                )}

                {/* Ações */}
                <td className={`${TD} whitespace-nowrap`}>
                    {!isEditing && (
                        <div className="flex items-center justify-center gap-1">
                            <button
                                onClick={() => onEdit(rule.payment_condition)}
                                className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-600 hover:bg-orange-100 hover:text-orange-700"
                            >
                                <PiPencilSimpleBold className="w-3 h-3" />
                                Editar
                            </button>
                            <button
                                onClick={() => {
                                    if (rule.id && confirm(`Remover "${rule.payment_label}"?`))
                                        onDelete(rule.id);
                                }}
                                disabled={isDeleting}
                                className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-100 hover:text-red-800 disabled:opacity-40"
                            >
                                <PiTrashBold className="w-3 h-3" />
                            </button>
                        </div>
                    )}
                </td>
            </tr>

            {/* Painel de edição — linha expandível abaixo */}
            {isEditing && (
                <tr className="bg-orange-50 border-b-2 border-orange-200">
                    <td colSpan={TOTAL_COLS} className="px-4 py-4">
                        <div className="flex flex-wrap items-end gap-6">
                            <div>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                                    Tetos de desconto por nível
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <FieldEdit
                                        label="Teto Vendedor"
                                        value={rule.seller_alcada}
                                        suffix="%"
                                        onChange={(v) => onChange('seller_alcada', v)}
                                    />
                                    <FieldEdit
                                        label="Teto Gerência"
                                        value={rule.manager_alcada}
                                        suffix="%"
                                        onChange={(v) => {
                                            onChange('manager_alcada', v);
                                            onChange('discount_floor', v);
                                        }}
                                    />
                                    <FieldEdit
                                        label="Teto Diretoria"
                                        value={rule.director_alcada}
                                        suffix="%"
                                        onChange={(v) => onChange('director_alcada', v)}
                                    />
                                </div>
                            </div>

                            <div>
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                                    Comissões
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <FieldEdit
                                        label="Comissão Base"
                                        value={rule.seller_base_rate}
                                        isRate
                                        suffix="%"
                                        onChange={(v) => onChange('seller_base_rate', v)}
                                    />
                                    <FieldEdit
                                        label="Comissão Mínima"
                                        value={rule.director_adjusted_rate}
                                        isRate
                                        suffix="%"
                                        onChange={(v) => onChange('director_adjusted_rate', v)}
                                    />
                                </div>
                            </div>

                            {/* Preview das faixas computadas */}
                            <div className="flex-1 min-w-[200px]">
                                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">
                                    Faixas calculadas (preview)
                                </p>
                                <div className="flex flex-wrap gap-1">
                                    <span className="rounded bg-green-100 text-green-800 px-1.5 py-0.5 text-[10px] font-semibold">
                                        0% a {fmtN(rule.manager_alcada)}% → {fmtPct(rule.seller_base_rate)}
                                    </span>
                                    {computeSteps(rule).map((s, i) => (
                                        <span
                                            key={i}
                                            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold text-white ${
                                                s.isMin ? 'bg-red-800' : 'bg-red-600'
                                            }`}
                                        >
                                            {fmtN(s.low)}% a {fmtN(s.high)}% → {fmtPct(s.rate)}
                                            {s.isMin ? ' ★' : ''}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <div className="flex items-center gap-2 self-end">
                                <button
                                    onClick={onSave}
                                    disabled={isSaving}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                                >
                                    <PiFloppyDiskBold className="w-4 h-4" />
                                    {isSaving ? 'Salvando…' : 'Salvar'}
                                </button>
                                <button
                                    onClick={onCancel}
                                    className="inline-flex items-center gap-1 rounded-lg bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
                                >
                                    <PiXBold className="w-4 h-4" />
                                    Cancelar
                                </button>
                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

export default function CommissionPaymentRulesPage() {
    const qc = useQueryClient();
    const [rules, setRules] = useState<PaymentRule[]>([]);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editDraft, setEditDraft] = useState<PaymentRule | null>(null);
    const [showCreate, setShowCreate] = useState(false);
    const [newRule, setNewRule] = useState<Partial<PaymentRule>>({
        payment_condition: '',
        payment_label: '',
        installments_max: 12,
        seller_alcada: 20,
        seller_base_rate: 0.025,
        manager_alcada: 25,
        director_alcada: 30,
        director_adjusted_rate: 0.02,
    });

    const { data, isPending, isError, error } = useQuery<{ data: PaymentRule[] }>({
        queryKey: ['commissioning', 'payment-rules'],
        queryFn: () => api.get('/commissioning/payment-rules').then((r) => r.data),
        retry: 1,
    });

    useEffect(() => {
        if (data?.data) setRules(data.data);
    }, [data]);

    function startEdit(id: string) {
        const rule = rules.find((r) => r.payment_condition === id);
        if (rule) {
            setEditDraft({ ...rule });
            setEditingId(id);
        }
    }

    function cancelEdit() {
        if (editDraft) {
            setRules((prev) =>
                prev.map((r) => (r.payment_condition === editDraft.payment_condition ? editDraft : r)),
            );
        }
        setEditingId(null);
        setEditDraft(null);
    }

    function updateDraft(field: keyof PaymentRule, value: unknown) {
        setRules((prev) =>
            prev.map((r) => (r.payment_condition === editingId ? { ...r, [field]: value } : r)),
        );
    }

    const saveMutation = useMutation({
        mutationFn: (rule: PaymentRule) =>
            api.put('/commissioning/payment-rules', { rules: [rule] }),
        onSuccess: (res) => {
            toast.success('Regra salva!');
            setRules(res.data.data);
            setEditingId(null);
            setEditDraft(null);
            qc.invalidateQueries({ queryKey: ['commissioning', 'payment-rules'] });
        },
        onError: () => {
            if (editDraft) {
                setRules((prev) =>
                    prev.map((r) =>
                        r.payment_condition === editDraft.payment_condition ? editDraft : r,
                    ),
                );
            }
            toast.error('Erro ao salvar. Verifique os dados.');
        },
    });

    const createMutation = useMutation({
        mutationFn: (rule: Partial<PaymentRule>) =>
            api.post('/commissioning/payment-rules', rule),
        onSuccess: (res) => {
            toast.success('Regra criada!');
            setRules(res.data.data);
            setShowCreate(false);
            setNewRule({
                payment_condition: '',
                payment_label: '',
                installments_max: 12,
                seller_alcada: 20,
                seller_base_rate: 0.025,
                manager_alcada: 25,
                director_alcada: 30,
                director_adjusted_rate: 0.02,
            });
            qc.invalidateQueries({ queryKey: ['commissioning', 'payment-rules'] });
        },
        onError: (err: { response?: { data?: { message?: string; errors?: Record<string, string[]> } } }) => {
            const msg =
                err?.response?.data?.errors
                    ? Object.values(err.response.data.errors).flat().join(' | ')
                    : err?.response?.data?.message ?? 'Erro ao criar regra.';
            toast.error(msg);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: number) =>
            api.delete(`/commissioning/payment-rules/${id}`),
        onSuccess: (res) => {
            toast.success('Regra removida!');
            setRules(res.data.data);
            qc.invalidateQueries({ queryKey: ['commissioning', 'payment-rules'] });
        },
        onError: () => toast.error('Erro ao remover regra.'),
    });

    function handleCreate() {
        createMutation.mutate(newRule);
    }

    function handleSave() {
        const rule = rules.find((r) => r.payment_condition === editingId);
        if (rule) saveMutation.mutate(rule);
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title="Regras de Comissão por Pagamento"
                subtitle="Faixas de desconto e taxas conforme condição de pagamento"
            >
                <button
                    onClick={() => { setShowCreate(true); setEditingId(null); }}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-[#1a2744] px-3 py-2 text-sm font-semibold text-white hover:bg-[#263359]"
                >
                    <PiPlusBold className="w-4 h-4" />
                    Nova Condição
                </button>
            </PageHeader>

            {/* Formulário de criação */}
            {showCreate && (
                <div className="rounded-xl border-2 border-dashed border-[#1a2744] bg-[#f0f3ff] px-5 py-5 space-y-4">
                    <h3 className="text-sm font-bold text-[#1a2744]">Nova Condição de Pagamento</h3>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">
                        <label className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Chave (payment_condition)</span>
                            <input
                                type="text"
                                placeholder="ex: ate_36x"
                                value={newRule.payment_condition ?? ''}
                                onChange={(e) => setNewRule((p) => ({ ...p, payment_condition: e.target.value }))}
                                className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-[#1a2744] focus:outline-none"
                            />
                        </label>
                        <label className="flex flex-col gap-0.5 col-span-1 sm:col-span-2 lg:col-span-1">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Rótulo (label)</span>
                            <input
                                type="text"
                                placeholder="ex: Até 36x"
                                value={newRule.payment_label ?? ''}
                                onChange={(e) => setNewRule((p) => ({ ...p, payment_label: e.target.value }))}
                                className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm focus:border-[#1a2744] focus:outline-none"
                            />
                        </label>
                        <label className="flex flex-col gap-0.5">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Parcelas máx.</span>
                            <input
                                type="number"
                                min={1}
                                value={newRule.installments_max ?? 12}
                                onChange={(e) => setNewRule((p) => ({ ...p, installments_max: parseInt(e.target.value) }))}
                                className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-center focus:border-[#1a2744] focus:outline-none"
                            />
                        </label>
                    </div>
                    <div className="flex flex-wrap gap-5">
                        <FieldEdit
                            label="Teto Vendedor"
                            value={newRule.seller_alcada ?? 0}
                            suffix="%"
                            onChange={(v) => setNewRule((p) => ({ ...p, seller_alcada: v }))}
                        />
                        <FieldEdit
                            label="Teto Gerência"
                            value={newRule.manager_alcada ?? 0}
                            suffix="%"
                            onChange={(v) => setNewRule((p) => ({ ...p, manager_alcada: v }))}
                        />
                        <FieldEdit
                            label="Teto Diretoria"
                            value={newRule.director_alcada ?? 0}
                            suffix="%"
                            onChange={(v) => setNewRule((p) => ({ ...p, director_alcada: v }))}
                        />
                        <FieldEdit
                            label="Comissão Base"
                            value={newRule.seller_base_rate ?? 0}
                            isRate
                            suffix="%"
                            onChange={(v) => setNewRule((p) => ({ ...p, seller_base_rate: v }))}
                        />
                        <FieldEdit
                            label="Comissão Mínima"
                            value={newRule.director_adjusted_rate ?? 0}
                            isRate
                            suffix="%"
                            onChange={(v) => setNewRule((p) => ({ ...p, director_adjusted_rate: v }))}
                        />
                    </div>
                    <div className="flex items-center gap-2 pt-1">
                        <button
                            onClick={handleCreate}
                            disabled={createMutation.isPending}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-[#1a2744] px-4 py-2 text-sm font-semibold text-white hover:bg-[#263359] disabled:opacity-50"
                        >
                            <PiFloppyDiskBold className="w-4 h-4" />
                            {createMutation.isPending ? 'Criando…' : 'Criar Regra'}
                        </button>
                        <button
                            onClick={() => setShowCreate(false)}
                            className="inline-flex items-center gap-1 rounded-lg bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
                        >
                            <PiXBold className="w-4 h-4" />
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800">
                <PiInfoBold className="mt-0.5 w-4 h-4 flex-shrink-0 text-blue-500" />
                <span>
                    Para cada 1% de desconto <strong>acima do teto de gerência</strong>, a comissão cai <strong>0,1 p.p.</strong> até o mínimo da Diretoria.
                    Clique em <em>Editar</em> para ajustar os tetos e taxas de uma condição.
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-400 border border-green-500" />
                    Vendedor
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-400 border border-yellow-500" />
                    Gerência
                </span>
                <span className="flex items-center gap-1">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-600 border border-red-700" />
                    Diretoria (escalonado)
                </span>
            </div>

            {isPending && <LoadingState variant="table" />}

            {isError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <strong>Erro ao carregar:</strong>{' '}
                    {(error as { response?: { data?: { message?: string } } })?.response?.data
                        ?.message ?? String(error)}
                </div>
            )}

            {!isPending && !isError && rules.length === 0 && (
                <p className="text-sm text-gray-500">Nenhuma regra encontrada.</p>
            )}

            {rules.length > 0 && (
                <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr>
                                <th className={`${TH_NAVY} text-left pl-4`} rowSpan={2}>
                                    Condição de Pagamento
                                </th>
                                <th className={TH_NAVY} rowSpan={2}>
                                    <div>Teto Desc.</div>
                                    <div className="text-[10px] opacity-70 font-normal">Vendedor</div>
                                </th>
                                <th className={`${TH_NAVY} bg-yellow-600 border-yellow-500`} rowSpan={2}>
                                    <div>Teto Desc.</div>
                                    <div className="text-[10px] opacity-80 font-normal">Gerência</div>
                                </th>
                                <th className={`${TH_NAVY} bg-[#1e6b38] border-[#175530]`} rowSpan={2}>
                                    <div>Comissão Base</div>
                                    <div className="text-[10px] opacity-70 font-normal">0% ao teto</div>
                                </th>
                                {Array.from({ length: NUM_STEPS }).map((_, i) => (
                                    <th
                                        key={i}
                                        colSpan={2}
                                        className={`${TH_NAVY} bg-[#8b1a1a] border-[#6b1212]`}
                                    >
                                        Diretoria — Step {i + 1}
                                    </th>
                                ))}
                                <th className={TH_NAVY} rowSpan={2}>
                                    Ações
                                </th>
                            </tr>
                            <tr>
                                {Array.from({ length: NUM_STEPS }).map((_, i) => (
                                    <React.Fragment key={i}>
                                        <th className={`${TH_NAVY} bg-[#7a1616] text-[10px] py-1.5`}>
                                            Faixa
                                        </th>
                                        <th className={`${TH_NAVY} bg-[#9b1e1e] text-[10px] py-1.5`}>
                                            Taxa
                                        </th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rules.map((rule) => (
                                <RuleRow
                                    key={rule.payment_condition}
                                    rule={rule}
                                    editingId={editingId}
                                    onEdit={startEdit}
                                    onCancel={cancelEdit}
                                    onChange={updateDraft}
                                    onSave={handleSave}
                                    onDelete={(id) => deleteMutation.mutate(id)}
                                    isSaving={saveMutation.isPending}
                                    isDeleting={deleteMutation.isPending}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-[11px] text-gray-400">
                ★ = taxa mínima (teto Diretoria). Os steps de diretoria são calculados automaticamente:
                taxa = máx(mínima, base − i × 0,1 p.p.) para cada 1% de desconto acima do teto de gerência.
            </p>
        </div>
    );
}
